import { useEffect, useMemo, useState } from 'react';
import { useGetCalculationQuery } from '../../CalculationDetail/apiCalculationDetail';
import { CalculationsCollection } from '../../../swagger/collections';
import { CalculationDetailResponse, CalculationMode } from '@ydistri/api-sdk';
import { CalculationDataType, CalculationInfo } from '../calculationsTypes';

export interface CalculationSourcesData {
  isPending: boolean;
  sources: CalculationSources;
}

export interface CalculationSources {
  manualCalculations?: CalculationInfo[];
  templates?: CalculationInfo[];
  overlappingCalculations?: CalculationDataType[];
}

interface SubordinateSourcesResult {
  key: string;
  manualCalculations: CalculationInfo[];
  templates: CalculationInfo[];
}

/**
 * Returns a list of calculations that were used when creating the calculation.
 * Merged calculation may have been created using a manual calculation and/or one or more templates.
 * When a calculation is created, these sources are used to create new calculations. These are then
 * listed in subordinatedCalculations property of the calculation.
 * This hooks returns a list of either OriginalCalculationId or SourceTemplateId of these submerged calculations.
 */
const useMergedCalculationSources = (calculationId: number): CalculationSourcesData => {
  const { data: calculation, isFetching } = useGetCalculationQuery(calculationId);

  const [subordinateSources, setSubordinateSources] = useState<SubordinateSourcesResult>({
    key: '',
    manualCalculations: [],
    templates: [],
  });

  const subordinateCalculationIds = useMemo(
    () => calculation?.subordinateCalculations?.map(({ id }) => id) ?? [],
    [calculation?.subordinateCalculations],
  );
  const subordinateCalculationIdsKey = subordinateCalculationIds.join(',');

  useEffect(() => {
    if (isFetching || !calculation || subordinateCalculationIds.length === 0) {
      return;
    }

    let isCancelled = false;

    Promise.all<CalculationDetailResponse>(
      subordinateCalculationIds.map(id =>
        CalculationsCollection.getCalculation(id).then(r => r.data.data),
      ),
    ).then(subOrdinateCalculations => {
      if (isCancelled) {
        return;
      }

      const manualCalculations = subOrdinateCalculations
        .filter(subOrdinateCalculation => subOrdinateCalculation.mode === CalculationMode.Manual)
        .map(mc => ({
          id: mc.originalCalculation?.id ?? 0,
          title: mc.originalCalculation?.title ?? '',
        }))
        .filter(mc => mc.id);

      const templates = subOrdinateCalculations.reduce<CalculationInfo[]>(
        (result, subOrdinateCalculation) => {
          if (
            subOrdinateCalculation.mode === CalculationMode.Computed &&
            subOrdinateCalculation.sourceTemplate
          ) {
            result.push({
              id: subOrdinateCalculation.sourceTemplate.id,
              title: subOrdinateCalculation.sourceTemplate.title,
            });
          }
          return result;
        },
        [],
      );

      setSubordinateSources({
        key: subordinateCalculationIdsKey,
        manualCalculations,
        templates,
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [calculation, isFetching, subordinateCalculationIds, subordinateCalculationIdsKey]);

  const overlappingCalculations = useMemo<CalculationDataType[]>(
    () => [...(calculation?.overlappingCalculations ?? [])],
    [calculation?.overlappingCalculations],
  );

  const isSubordinatePending =
    !isFetching &&
    !!calculation &&
    subordinateCalculationIds.length > 0 &&
    subordinateSources.key !== subordinateCalculationIdsKey;

  const sources = useMemo<CalculationSources>(() => {
    const isLatestSubordinateState = subordinateSources.key === subordinateCalculationIdsKey;

    return {
      manualCalculations: isLatestSubordinateState ? subordinateSources.manualCalculations : [],
      templates: isLatestSubordinateState ? subordinateSources.templates : [],
      overlappingCalculations:
        overlappingCalculations.length > 0 ? overlappingCalculations : undefined,
    };
  }, [overlappingCalculations, subordinateCalculationIdsKey, subordinateSources]);

  return useMemo(() => {
    return {
      isPending: isFetching || isSubordinatePending,
      sources,
    };
  }, [isFetching, isSubordinatePending, sources]);
};

export default useMergedCalculationSources;
